/** @jsxRuntime classic */
/** @jsx jsx */
import { useState, useCallback, useEffect, useMemo, forwardRef } from 'react';
import { css, jsx } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';

import Text from '@mui/material/Typography';

import TodayOutlinedIcon from '@mui/icons-material/TodayOutlined';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import PersonIcon from '@mui/icons-material/Person';
import SourceOutlinedIcon from '@mui/icons-material/SourceOutlined';
import LangageIcon from '@mui/icons-material/Language';

import Poster from '../../common/Poster';
import SpeakerList from '../../common/SpeakerList';

import { SOURCES } from '../../../constants';
import { useSupportedLanguages } from '../../../utils/hooks';

function InfoWrapperEl({ icon: Icon, label, value }) {
  return (
    <div
      css={css`
        display: flex;
        align-items: center;
        min-width: 0;
      `}
    >
      <Icon
        css={css`
          color: #0000008a;
          margin-right: 4px;
        `}
        fontSize="12px"
      />
      <Text
        css={css`
          color: #0000008a;
        `}
        fontSize="0.9rem"
        variant="caption"
      >
        {label}:
      </Text>
      <Text
        variant="caption"
        fontWeight="bold"
        fontSize="0.9rem"
        css={css`
          margin-left: 2px;
          color: var(--text-primary);
          text-overflow: ellipsis;
          overflow: hidden;
          white-space: nowrap;
        `}
      >
        {value}
      </Text>
    </div>
  );
}

export default function InfoBlock({ meeting }) {
  const { t, i18n } = useTranslation();
  const supportedLanguages = useSupportedLanguages();
  const formattedDate = useMemo(() => {
    if (!meeting?.created_at) {
      return '';
    }
    return DateTime.fromISO(meeting?.created_at)
      .setLocale(i18n.language)
      .toLocaleString(DateTime.DATE_FULL);
  }, [meeting, i18n?.language]);

  const lang = meeting?.auto_lang_id
    ? `${supportedLanguages[meeting?.language]} (${t('Automatiskt')})`
    : supportedLanguages[meeting?.language] || t('Automatiskt');

  return (
    <div>
      <div
        css={css`
          margin-bottom: 16px;
          display: flex;
          overflow: hidden;
        `}
      >
        <Poster meeting={meeting} width={137} height={91} />
        <div
          css={css`
            min-width: 0;
          `}
        >
          <Text
            variant="h6"
            fontWeight="bold"
            fontSize="1.125rem"
            sx={{ marginBottom: '0.5rem' }}
          >
            {meeting.name}
          </Text>
          <SpeakerList meeting={meeting} />
          <div
            css={css`
              display: flex;
              flex-wrap: wrap;
              gap: 8px 16px;
              margin-top: 0.5rem;
            `}
          >
            <InfoWrapperEl
              icon={TodayOutlinedIcon}
              label={t('Skapad')}
              value={formattedDate}
            />
            <InfoWrapperEl
              icon={PersonIcon}
              label={t('Ägare')}
              value={meeting.owner?.email}
            />
            <InfoWrapperEl
              icon={SourceOutlinedIcon}
              label={t('Källa')}
              value={SOURCES[meeting.source]}
            />
            <InfoWrapperEl icon={LangageIcon} label={t('Språk')} value={lang} />
          </div>
        </div>
      </div>
    </div>
  );
}
